// HOW-SLOTS-WEB-AGENDA-TASKS (2026-07-09): tasks thunks. Same unaliasId(coId) discipline as
// agendaActions.js -- callers pass the FE's aliased int CO id, the network call uses the real
// dev-backend uuid.
import actionTypes from "@actions/actionTypes"
import { unaliasId } from "@utils/devBackendShim"
import {
  listTasks,
  createTask,
  updateTask,
  updateTaskPeople,
  completeTask,
  uncompleteTask,
  claimTask,
  deleteTask,
  fetchMyTasks,
  ConflictError,
  ClaimRaceError,
} from "@utils/agendaTasksApi"

export const loadTasks = coId => async dispatch => {
  if (!coId) return
  dispatch({ type: actionTypes.TASKS_SET_LOADING, payload: { coId, loading: true } })
  const items = await listTasks(unaliasId(coId))
  dispatch({ type: actionTypes.TASKS_SET_ITEMS, payload: { coId, items } })
}

export const addTask = (coId, fields) => async dispatch => {
  if (!coId) return null
  const task = await createTask(unaliasId(coId), fields)
  if (task) dispatch({ type: actionTypes.TASK_UPSERTED, payload: { coId, task } })
  return task
}

export const editTask =
  (coId, taskId, fields, expectedVersion, { onConflict } = {}) =>
  async dispatch => {
    if (!coId || !taskId) return null
    try {
      const task = await updateTask(taskId, fields, expectedVersion)
      if (task) dispatch({ type: actionTypes.TASK_UPSERTED, payload: { coId, task } })
      return task
    } catch (e) {
      if (e instanceof ConflictError) {
        const items = await listTasks(unaliasId(coId))
        dispatch({ type: actionTypes.TASKS_SET_ITEMS, payload: { coId, items } })
        if (onConflict) onConflict(items)
        return null
      }
      throw e
    }
  }

// WU-P2-FE-a (2026-07-09) -- people-picker thunk. fields = {assignee_id?, involved_people?}
// (assignee_id is a participants.user_id uuid, or explicit null to clear; involved_people is the
// FULL new array, or [] to clear). Same 409 version_conflict -> refetch + onConflict convention.
export const setTaskPeople =
  (coId, taskId, fields, expectedVersion, { onConflict } = {}) =>
  async dispatch => {
    if (!coId || !taskId) return null
    try {
      const task = await updateTaskPeople(taskId, fields, expectedVersion)
      if (task) dispatch({ type: actionTypes.TASK_UPSERTED, payload: { coId, task } })
      return task
    } catch (e) {
      if (e instanceof ConflictError) {
        const items = await listTasks(unaliasId(coId))
        dispatch({ type: actionTypes.TASKS_SET_ITEMS, payload: { coId, items } })
        if (onConflict) onConflict(items)
        return null
      }
      throw e
    }
  }

export const toggleTaskComplete =
  (coId, task, { onConflict } = {}) =>
  async dispatch => {
    if (!coId || !task) return null
    try {
      const updated = task.completed_at
        ? await uncompleteTask(task.id, task.version)
        : await completeTask(task.id, task.version)
      if (updated) dispatch({ type: actionTypes.TASK_UPSERTED, payload: { coId, task: updated } })
      return updated
    } catch (e) {
      if (e instanceof ConflictError) {
        const items = await listTasks(unaliasId(coId))
        dispatch({ type: actionTypes.TASKS_SET_ITEMS, payload: { coId, items } })
        if (onConflict) onConflict(items)
        return null
      }
      throw e
    }
  }

// onClaimLost() fires on a 409 claim_race ("already claimed" UX + rollback -- the caller's
// optimistic UI, if any, reverts because the store re-syncs from the server here).
export const claimTaskThunk =
  (coId, taskId, { onClaimLost } = {}) =>
  async dispatch => {
    if (!coId || !taskId) return null
    try {
      const task = await claimTask(taskId)
      if (task) dispatch({ type: actionTypes.TASK_UPSERTED, payload: { coId, task } })
      return task
    } catch (e) {
      if (e instanceof ClaimRaceError) {
        const items = await listTasks(unaliasId(coId))
        dispatch({ type: actionTypes.TASKS_SET_ITEMS, payload: { coId, items } })
        if (onClaimLost) onClaimLost(items)
        return null
      }
      throw e
    }
  }

export const removeTask = (coId, taskId) => async dispatch => {
  if (!coId || !taskId) return false
  const ok = await deleteTask(taskId)
  if (ok) dispatch({ type: actionTypes.TASK_REMOVED, payload: { coId, taskId } })
  return ok
}

export const loadMyTasks = () => async dispatch => {
  dispatch({ type: actionTypes.MY_TASKS_SET_LOADING, payload: true })
  const items = await fetchMyTasks()
  dispatch({ type: actionTypes.MY_TASKS_SET, payload: items })
}
